import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { FormikConsumer } from 'formik';
import {
  ButtonDelete,
  CrudContext,
  FormatText,
  FormInput,
  FormSelect,
  FormSubmitButton,
  GridView,
  MasterDetailForm,
  OptionProps,
  useRouter,
  Widget,
} from '@digital/ui';
import * as organogramaApi from '@app/api/organogramaGestor';
import { getAllAreasByDiretoriaOptions } from '@app/api/area';
import { getAllDepartamentoProAreaIdOptions, getAllDepartamentoProDiretoriaIdOptions } from '@app/api/departamento';
import { getAllDiretoriasOptions } from '@app/api/diretoria';
import { getAllGestoresOptions } from '@app/api/usuario';
import { hasPermission } from '@app/helpers/permissions';
import ImportarPlanilha from './ImportarPlanilha';
import OrganogramaMasterDetailView from './OrganogramaMasterDetailView';

const EstruturaOrganizacionalFields = ({ selectedItem }: { selectedItem?: organogramaApi.OrganogramaGestor }) => {
  const selectedItemId = selectedItem ? selectedItem.id : undefined;
  const selectedDiretoriaId = selectedItem ? selectedItem.diretoriaId : undefined;
  const selectedAreaId = selectedItem ? selectedItem.areaId : undefined;
  const [diretoriaId, setDiretoriaId] = useState<string>();
  const [areaId, setAreaId] = useState<string>();
  const [areas, setAreas] = useState<OptionProps[]>([]);
  const [departamentos, setDepartamentos] = useState<OptionProps[]>([]);

  useEffect(() => {
    setDiretoriaId(selectedDiretoriaId);
    setAreaId(selectedAreaId);
  }, [selectedItemId, selectedDiretoriaId, selectedAreaId]);

  useEffect(() => {
    let ativo = true;

    if (!diretoriaId) {
      setAreas([]);
      return undefined;
    }

    getAllAreasByDiretoriaOptions(diretoriaId).then(opcoes => {
      if (ativo) setAreas(opcoes);
    });

    return () => {
      ativo = false;
    };
  }, [diretoriaId]);

  useEffect(() => {
    let ativo = true;

    if (!diretoriaId) {
      setDepartamentos([]);
      return undefined;
    }

    const carregar = areaId
      ? getAllDepartamentoProAreaIdOptions(areaId)
      : getAllDepartamentoProDiretoriaIdOptions(diretoriaId);
    carregar.then(opcoes => {
      if (ativo) setDepartamentos(opcoes);
    });

    return () => {
      ativo = false;
    };
  }, [areaId, diretoriaId]);

  return (
    <FormikConsumer>
      {({ setFieldValue }) => (
        <>
          <FormSelect
            showSearch
            optionFilterProp="children"
            label="Diretoria"
            name="diretoriaId"
            placeholder="Selecione a diretoria"
            getOptions={getAllDiretoriasOptions}
            onChange={value => {
              setDiretoriaId(value ? value.toString() : undefined);
              setAreaId(undefined);
              setFieldValue('areaId', undefined);
              setFieldValue('departamentoId', undefined);
            }}
            required
            span={4}
          />
          <FormSelect
            showSearch
            optionFilterProp="children"
            label="Área"
            name="areaId"
            placeholder="Selecione a área"
            options={areas}
            disabled={!diretoriaId}
            allowClear
            onChange={value => {
              setAreaId(value ? value.toString() : undefined);
              setFieldValue('departamentoId', undefined);
            }}
            span={4}
          />
          <FormSelect
            showSearch
            optionFilterProp="children"
            label="Departamento"
            name="departamentoId"
            placeholder="Selecione o departamento"
            options={departamentos}
            disabled={!diretoriaId}
            allowClear
            span={4}
          />
        </>
      )}
    </FormikConsumer>
  );
};

const OrganogramaGestor = () => {
  const [getAllItems, setGetAllItems] = useState(() => organogramaApi.getAllOrganogramaGestores);
  const { history } = useRouter();

  const recarregar = () => {
    setGetAllItems(() => () => organogramaApi.getAllOrganogramaGestores());
  };

  const criar = (values: any) =>
    organogramaApi.createOrganogramaGestor(values).then(resp => {
      if (resp && resp.valid !== false) recarregar();
      return resp;
    });

  const atualizar = (values: any) =>
    organogramaApi.updateOrganogramaGestor(values).then(resp => {
      if (resp && resp.valid !== false) recarregar();
      return resp;
    });

  return (
    <OrganogramaMasterDetailView
      toolbar={
        hasPermission(['ORGANOGRAMA_GESTOR_CADASTRAR']) &&
        hasPermission(['ORGANOGRAMA_GESTOR_EDITAR']) && <ImportarPlanilha onImported={recarregar} />
      }
      getAllItems={getAllItems}
      createItem={hasPermission(['ORGANOGRAMA_GESTOR_CADASTRAR']) ? criar : undefined}
      updateItem={hasPermission(['ORGANOGRAMA_GESTOR_EDITAR']) ? atualizar : undefined}
      titleExtractor={item => item.gestor}
      detailTitleExtractor={item =>
        `${item.gestor} - ${item.siglaArea || item.area || item.siglaDiretoria || item.diretoria}`
      }
      descriptionExtractor={item =>
        `${item.siglaDiretoria || item.diretoria}` +
        (item.areaId ? ` / ${item.siglaArea || item.area || ''}` : '') +
        (item.departamento ? ` / ${item.siglaDepartamento || item.departamento}` : '')
      }
      filterPredicate={(item, value) => {
        const termo = value.toLowerCase();
        return (
          (item.gestor || '').toLowerCase().includes(termo) ||
          (item.email || '').toLowerCase().includes(termo) ||
          (item.diretoria || '').toLowerCase().includes(termo) ||
          (item.area || '').toLowerCase().includes(termo) ||
          (item.siglaArea || '').toLowerCase().includes(termo) ||
          (item.departamento || '').toLowerCase().includes(termo)
        );
      }}>
      <CrudContext.Consumer>
        {({ selectedItem }) => (
          <GridView noform>
            <MasterDetailForm
              validationSchema={Yup.object().shape({
                usuarioId: Yup.string().required('Gestor deve ser informado'),
                diretoriaId: Yup.string().required('Diretoria deve ser informada'),
              })}>
              <Widget
                title="Estrutura organizacional do gestor"
                actions={[
                  selectedItem && hasPermission(['ORGANOGRAMA_GESTOR_DELETAR']) && (
                    <ButtonDelete
                      type="danger"
                      confirm="Deseja realmente excluir este gestor do organograma?"
                      okText="Confirmar"
                      onClick={() =>
                        organogramaApi.deleteOrganogramaGestor(selectedItem.id).then(resp => {
                          if (resp && resp.valid !== false) {
                            recarregar();
                            history.push('/organogramaGestores');
                          }
                          return resp;
                        })
                      }
                    />
                  ),
                  hasPermission(['ORGANOGRAMA_GESTOR_CADASTRAR', 'ORGANOGRAMA_GESTOR_EDITAR']) && (
                    <FormSubmitButton noIcon type="primary">
                      <FormatText id="ui.commons.save" uppercase />
                    </FormSubmitButton>
                  ),
                ]}>
                <FormSelect
                  showSearch
                  optionFilterProp="children"
                  label="Gestor"
                  name="usuarioId"
                  placeholder="Selecione o gestor"
                  getOptions={getAllGestoresOptions}
                  disabled={!!selectedItem}
                  required
                  span={12}
                />
                <EstruturaOrganizacionalFields selectedItem={selectedItem} />
                <FormInput label="E-mail" name="email" disabled span={6} />
                <FormInput label="Usuário" name="userName" disabled span={3} />
                <FormInput label="Sigla da área" name="siglaArea" disabled span={3} />
                <FormInput label="Cargo" name="cargo" disabled span={6} />
              </Widget>
            </MasterDetailForm>
          </GridView>
        )}
      </CrudContext.Consumer>
    </OrganogramaMasterDetailView>
  );
};

export default OrganogramaGestor;
